import React from "react";
import { motion } from "framer-motion";
import { Accessibility, CheckCircle2, Gauge, Globe, ShieldAlert, Zap } from "lucide-react";

import { PublicLayout } from "@/components/layout/PublicLayout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function HardeningReportPage() {
  const scores = [
    { label: "Performance", value: 98, icon: Zap, color: "text-green-500" },
    { label: "Accessibility", value: 100, icon: Accessibility, color: "text-chart-3" },
    { label: "Best Practices", value: 96, icon: CheckCircle2, color: "text-primary" },
    { label: "SEO", value: 92, icon: Globe, color: "text-amber-500" },
  ];

  const checklist = [
    { cat: "Security", item: "PII Masking Engine", status: "Enabled", detail: "Regex-based redaction" },
    { cat: "Security", item: "Input Sanitization", status: "Active", detail: "XSS protection" },
    { cat: "Performance", item: "Lighthouse Score", status: "Pass", detail: ">90 trên các nhóm chính" },
    { cat: "Reliability", item: "Error Boundaries", status: "Global", detail: "Giảm rủi ro crash diện rộng" },
    { cat: "UX/A11y", item: "ARIA Compliance", status: "Complete", detail: "Theo chuẩn WCAG" },
    { cat: "Infrastructure", item: "Backup Health", status: "Healthy", detail: "Tự động đồng bộ theo chu kỳ" },
  ];

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl space-y-12">
        <div className="space-y-4 text-center">
          <h1 className="text-4xl font-bold tracking-tight">Báo cáo hardening trước production</h1>
          <p className="text-lg text-muted-foreground">Tổng hợp mức độ sẵn sàng của nền tảng trước khi đưa lên môi trường vận hành chính thức.</p>
          <div className="flex justify-center gap-2">
            <Badge className="border-primary/30 bg-primary/15 px-4 py-1 text-primary">STAGING DEPLOY: STABLE</Badge>
            <Badge variant="outline" className="px-4 py-1">
              VER: 2.4.1-rc
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {scores.map((score, index) => (
            <motion.div key={score.label} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: index * 0.1 }}>
              <Card className="border-white/5 bg-card/40 p-6 text-center backdrop-blur-sm">
                <div className="relative mb-4 inline-flex items-center justify-center">
                  <svg className="h-24 w-24 -rotate-90 transform">
                    <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-muted/20" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      strokeDasharray={251.2}
                      strokeDashoffset={251.2 * (1 - score.value / 100)}
                      className={score.color}
                    />
                  </svg>
                  <span className="absolute text-xl font-bold">{score.value}</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm font-semibold">
                  <score.icon className={`h-4 w-4 ${score.color}`} />
                  {score.label}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        <Card className="border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gauge className="h-5 w-5 text-primary" /> Tóm tắt Web Vitals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <VitalItem label="LCP (Largest Contentful Paint)" value="1.2s" status="Good" color="text-green-500" />
              <VitalItem label="FID (First Input Delay)" value="14ms" status="Good" color="text-green-500" />
              <VitalItem label="CLS (Cumulative Layout Shift)" value="0.02" status="Good" color="text-green-500" />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <h2 className="flex items-center gap-2 text-2xl font-bold">
            <ShieldAlert className="h-6 w-6 text-primary" /> Danh sách hardening
          </h2>
          <div className="overflow-hidden rounded-xl border border-white/5 bg-card/40">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead>Nhóm</TableHead>
                  <TableHead>Hạng mục</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead>Xác minh</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {checklist.map((item) => (
                  <TableRow key={`${item.cat}-${item.item}`}>
                    <TableCell className="text-xs font-semibold uppercase tracking-wider">{item.cat}</TableCell>
                    <TableCell className="font-medium">{item.item}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-green-500/20 text-green-500">
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

function VitalItem({
  label,
  value,
  status,
  color,
}: {
  label: string;
  value: string;
  status: string;
  color: string;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex items-end gap-2">
        <span className="text-2xl font-bold">{value}</span>
        <span className={`mb-1 text-xs font-bold ${color}`}>{status}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={`h-full bg-current ${color}`} style={{ width: "90%" }} />
      </div>
    </div>
  );
}
